import { useState, useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../App";
import { Button } from "@/components/ui/button";
import { MapPin, RefreshCw, BarChart3, FileText } from "lucide-react";
import { useTranslation } from 'react-i18next';
import { LoadingOverlay } from "./LoadingOverlay";

// Import refactored components
import {
  LocationFilters,
  LocationStats,
  LocationSummaryView,
  LocationDetailedView,
} from "./location-reports";

export default function LocationBasedReports({ user, activeTab }) {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Track previous tab to detect tab changes
  const prevTabRef = useRef(activeTab);
  
  // Storage key for session persistence (kept for reference but not used)
  const STORAGE_KEY = 'locationReportsState';
  
  // Get saved state from sessionStorage
  const getSavedState = () => {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  };
  
  // Initialize state - always use default values (no session persistence)
  // This ensures a fresh start every time the tab is selected
  
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  
  // Enabled locations (can be expanded in future)
  const ENABLED_DISTRICT = "RAMANAGARA";
  const ENABLED_TALUK = "MAGADI";
  
  // Location filters - Always start with defaults (no session/URL restoration)
  const [selectedDistrict, setSelectedDistrict] = useState(ENABLED_DISTRICT);
  const [selectedTaluk, setSelectedTaluk] = useState(ENABLED_TALUK);
  const [selectedHobli, setSelectedHobli] = useState('');
  const [selectedCircle, setSelectedCircle] = useState('');
  const [selectedVillage, setSelectedVillage] = useState('');
  
  // View mode: always start with summary view
  const [viewMode, setViewMode] = useState('summary');
  
  // Pagination state - always start at page 1
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 50;
  
  // Dropdown options from API (now contain full location objects with is_active, names, etc.)
  const [districts, setDistricts] = useState([]);
  const [taluks, setTaluks] = useState([]);
  const [hoblis, setHoblis] = useState([]);
  const [circles, setCircles] = useState([]);
  const [villages, setVillages] = useState([]);
  
  // Flag to track if we just restored from sessionStorage
  const restoredFromSessionRef = useRef(false);
  // Flag to track initial mount
  const isInitialMountRef = useRef(true);
  
  // Reset state when Location Reports tab becomes active
  useEffect(() => {
    // Only reset if we're switching TO this tab (not on initial mount)
    if (activeTab === 'location-reports' && prevTabRef.current !== 'location-reports') {
      // Reset all filters to default
      setSelectedHobli('');
      setSelectedCircle('');
      setSelectedVillage('');
      setViewMode('summary');
      setCurrentPage(1);
      
      // Clear URL params
      const params = new URLSearchParams(searchParams);
      params.delete('lr_hobli');
      params.delete('lr_circle');
      params.delete('lr_village');
      params.delete('lr_view');
      params.delete('lr_page');
      setSearchParams(params, { replace: true });
    }
    prevTabRef.current = activeTab;
  }, [activeTab]);
  
  // Session storage restoration disabled - Always start fresh when tab is selected
  // This provides a clean slate every time user navigates to Location Reports
  /*
  useEffect(() => {
    const saved = getSavedState();
    if (saved) {
      let didRestore = false;
      
      // Only restore if URL doesn't have the values
      if (!searchParams.get('lr_view') && saved.lr_view) {
        setViewMode(saved.lr_view);
        didRestore = true;
      }
      if (!searchParams.get('lr_page') && saved.lr_page) {
        const savedPage = parseInt(saved.lr_page, 10);
        if (savedPage > 1) {
          setCurrentPage(savedPage);
          didRestore = true;
        }
      }
      if (!searchParams.get('lr_hobli') && saved.lr_hobli) {
        setSelectedHobli(saved.lr_hobli);
        didRestore = true;
      }
      if (!searchParams.get('lr_village') && saved.lr_village) {
        setSelectedVillage(saved.lr_village);
        didRestore = true;
      }
      
      if (didRestore) {
        restoredFromSessionRef.current = true;
        // Reset the flag after a short delay
        setTimeout(() => {
          restoredFromSessionRef.current = false;
        }, 100);
      }
    }
  }, []); // Run only on mount
  */
  
  // Session storage save disabled - No need to persist state across tab switches
  /*
  useEffect(() => {
    const stateToSave = {
      lr_hobli: selectedHobli,
      lr_village: selectedVillage,
      lr_view: viewMode,
      lr_page: currentPage.toString()
    };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stateToSave));
  }, [selectedHobli, selectedVillage, viewMode, currentPage]);
  */
  
  // Update URL params when state changes (keeping this for direct URL sharing)
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    
    // Always keep the tab set to location-reports
    params.set('tab', 'location-reports');
    
    // Use lr_ prefix for Location Reports specific params to avoid conflicts
    if (selectedHobli) {
      params.set('lr_hobli', selectedHobli);
    } else {
      params.delete('lr_hobli');
    }
    if (selectedVillage) {
      params.set('lr_village', selectedVillage);
    } else {
      params.delete('lr_village');
    }
    if (viewMode !== 'summary') {
      params.set('lr_view', viewMode);
    } else {
      params.delete('lr_view');
    }
    if (currentPage > 1) {
      params.set('lr_page', currentPage.toString());
    } else {
      params.delete('lr_page');
    }
    
    setSearchParams(params, { replace: true });
  }, [selectedHobli, selectedVillage, viewMode, currentPage]);

  // Load locations from API on mount
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        // Fetch all districts (including disabled ones)
        const districtsRes = await api.get('/locations/districts?include_all=true');
        setDistricts(districtsRes.data);
        
        // Fetch taluks for default district
        const taluksRes = await api.get(`/locations/taluks?district=${ENABLED_DISTRICT}&include_all=true`);
        setTaluks(taluksRes.data);
        
        // Fetch hoblis for default taluk
        const hoblisRes = await api.get(`/locations/hoblis?district=${ENABLED_DISTRICT}&taluk=${ENABLED_TALUK}`);
        setHoblis(hoblisRes.data);
        
        // Load applications
        loadApplications();
      } catch (error) {
        console.error('Failed to load location data:', error);
        setLoading(false);
      }
    };
    loadInitialData();
  }, []);

  // Update taluks when district changes
  useEffect(() => {
    const loadTaluks = async () => {
      if (selectedDistrict) {
        try {
          const taluksRes = await api.get(`/locations/taluks?district=${selectedDistrict}&include_all=true`);
          setTaluks(taluksRes.data);
        } catch (error) {
          console.error('Failed to load taluks:', error);
        }
      } else {
        setTaluks([]);
      }
    };
    loadTaluks();
    // Don't reset taluk if it's the default enabled district
    if (selectedDistrict !== ENABLED_DISTRICT) {
      setSelectedTaluk("");
      setSelectedHobli("");
      setSelectedVillage("");
    }
  }, [selectedDistrict]);

  // Update hoblis when taluk changes
  useEffect(() => {
    const loadHoblis = async () => {
      if (selectedDistrict && selectedTaluk) {
        try {
          const hoblisRes = await api.get(`/locations/hoblis?district=${selectedDistrict}&taluk=${selectedTaluk}`);
          setHoblis(hoblisRes.data);
        } catch (error) {
          console.error('Failed to load hoblis:', error);
        }
      } else {
        setHoblis([]);
      }
    };
    loadHoblis();
    // Don't reset hobli if we just restored from session or on initial mount
    if (!restoredFromSessionRef.current && !isInitialMountRef.current) {
      setSelectedHobli("");
      setSelectedVillage("");
    }
  }, [selectedDistrict, selectedTaluk]);

  // Known spelling variations between locations API and database
  // This handles cases where the same hobli has different Kannada/English spellings
  // Madabal/Madabalu variants in Kannada:
  // - ಮಾಡಬಾಳ್ (with virama at end)
  // - ಮಾಡಬಾಳು (with u vowel at end)
  // - ಮಾದಬಾಳು (different consonant ದ vs ಡ)
  const HOBLI_SPELLING_VARIANTS = {
    // Kannada variants - all map to canonical 'ಮಾಡಬಾಳು'
    'ಮಾದಬಾಳು': 'ಮಾಡಬಾಳು',
    'ಮಾಡಬಾಳ್': 'ಮಾಡಬಾಳು',  // virama version -> standard
    'ಮಾಡಬಾಳು': 'ಮಾಡಬಾಳು',  // Self-mapping for consistency
    // English variants - Madabalu and Madabal are the same hobli
    'Madabal': 'Madabalu',
    'MADABAL': 'MADABALU',
    'madabal': 'madabalu',
  };

  // All Madabal/Madabalu variants for matching
  const MADABAL_VARIANTS = ['ಮಾಡಬಾಳು', 'ಮಾಡಬಾಳ್', 'ಮಾದಬಾಳು', 'Madabalu', 'Madabal', 'MADABALU', 'MADABAL'];

  // Helper function to get Kannada hobli name from English key
  // Database stores hobli in Kannada, but filters use English keys from locations API
  const getKannadaHobliName = (hobliKey) => {
    if (!hobliKey) return '';
    const hobliObj = hoblis.find(h => h.key?.toUpperCase() === hobliKey.toUpperCase());
    return hobliObj?.name_kn || hobliKey;
  };

  // Helper function to check if a hobli name is a Madabal/Madabalu variant
  const isMadabalVariant = (hobliName) => {
    if (!hobliName) return false;
    return MADABAL_VARIANTS.includes(hobliName) || 
           hobliName.toUpperCase() === 'MADABALU' || 
           hobliName.toUpperCase() === 'MADABAL';
  };

  // Helper function to get all possible spellings for a hobli (both English and Kannada)
  const getHobliVariants = (hobliKey) => {
    if (!hobliKey) return [];
    const kannadaName = getKannadaHobliName(hobliKey);
    const variants = new Set([hobliKey, kannadaName]);
    
    // Add known spelling variants
    if (HOBLI_SPELLING_VARIANTS[kannadaName]) {
      variants.add(HOBLI_SPELLING_VARIANTS[kannadaName]);
    }
    if (HOBLI_SPELLING_VARIANTS[hobliKey]) {
      variants.add(HOBLI_SPELLING_VARIANTS[hobliKey]);
    }
    
    // Handle Madabalu/Madabal specifically - add ALL variants
    if (isMadabalVariant(hobliKey) || isMadabalVariant(kannadaName)) {
      MADABAL_VARIANTS.forEach(v => variants.add(v));
    }
    
    return Array.from(variants);
  };

  // Helper function to check if app.hobli matches selected hobli (handles both English key and Kannada name)
  const matchesHobli = (appHobli, selectedHobliKey) => {
    if (!selectedHobliKey || !appHobli) return !selectedHobliKey;
    
    // Special case: if either is a Madabal variant, check if both are Madabal variants
    if (isMadabalVariant(selectedHobliKey) && isMadabalVariant(appHobli)) {
      return true;
    }
    
    // Get all possible spellings for the selected hobli
    const variants = getHobliVariants(selectedHobliKey);
    
    // Check if app.hobli matches any variant
    return variants.includes(appHobli) || 
           appHobli.toUpperCase() === selectedHobliKey.toUpperCase() ||
           appHobli === selectedHobliKey;
  };

  // Update circles when hobli changes - get unique circles from applications data
  useEffect(() => {
    if (selectedDistrict && selectedTaluk && selectedHobli) {
      // Get unique circles from applications for the selected hobli
      const uniqueCircles = [...new Set(
        applications
          .filter(app => 
            app.district?.toUpperCase() === selectedDistrict.toUpperCase() &&
            app.taluk?.toUpperCase() === selectedTaluk.toUpperCase() &&
            matchesHobli(app.hobli, selectedHobli) &&
            app.circle // Only include apps with circle
          )
          .map(app => app.circle)
      )].sort();
      
      // Convert to format expected by dropdown
      const circleOptions = uniqueCircles.map(c => ({
        key: c,
        name_en: c,
        name_kn: c
      }));
      
      setCircles(circleOptions);
    } else {
      setCircles([]);
    }
    // Reset circle and village when hobli changes
    if (!restoredFromSessionRef.current && !isInitialMountRef.current) {
      setSelectedCircle("");
      setSelectedVillage("");
    }
  }, [selectedDistrict, selectedTaluk, selectedHobli, applications, hoblis]);

  // Update villages when circle changes - get unique villages from applications data
  useEffect(() => {
    if (selectedDistrict && selectedTaluk && selectedHobli && selectedCircle) {
      // Get unique villages from applications for the selected circle
      const uniqueVillages = [...new Set(
        applications
          .filter(app => 
            app.district?.toUpperCase() === selectedDistrict.toUpperCase() &&
            app.taluk?.toUpperCase() === selectedTaluk.toUpperCase() &&
            matchesHobli(app.hobli, selectedHobli) &&
            app.circle === selectedCircle &&
            app.village // Only include apps with village
          )
          .map(app => app.village)
      )].sort();
      
      // Convert to format expected by dropdown (key and display name are the same for application data)
      const villageOptions = uniqueVillages.map(v => ({
        key: v,
        name_en: v,
        name_kn: v
      }));
      
      setVillages(villageOptions);
    } else if (selectedDistrict && selectedTaluk && selectedHobli && !selectedCircle) {
      // If no circle selected but hobli is selected, show all villages in that hobli
      const uniqueVillages = [...new Set(
        applications
          .filter(app => 
            app.district?.toUpperCase() === selectedDistrict.toUpperCase() &&
            app.taluk?.toUpperCase() === selectedTaluk.toUpperCase() &&
            matchesHobli(app.hobli, selectedHobli) &&
            app.village
          )
          .map(app => app.village)
      )].sort();
      
      const villageOptions = uniqueVillages.map(v => ({
        key: v,
        name_en: v,
        name_kn: v
      }));
      
      setVillages(villageOptions);
    } else {
      setVillages([]);
    }
    // Reset village when circle changes
    if (!restoredFromSessionRef.current && !isInitialMountRef.current) {
      setSelectedVillage("");
    }
  }, [selectedDistrict, selectedTaluk, selectedHobli, selectedCircle, applications, hoblis]);

  const loadApplications = async () => {
    setLoading(true);
    try {
      // Fetch all applications for location reports (use high limit to get all)
      // Applications in DB may use different casing, so we fetch broadly and filter in frontend
      const response = await api.get("/applications", {
        params: {
          limit: 10000,  // Fetch all applications for reporting
        }
      });
      setApplications(response.data);
    } catch (error) {
      console.error("Failed to load applications:", error);
    } finally {
      setLoading(false);
    }
  };

  // Filter applications based on selected location (case-insensitive comparison for English, exact match for Kannada)
  const filteredApplications = useMemo(() => {
    return applications.filter(app => {
      // Case-insensitive comparison for district, taluk (these are typically in English/uppercase)
      if (selectedDistrict && app.district?.toUpperCase() !== selectedDistrict.toUpperCase()) return false;
      if (selectedTaluk && app.taluk?.toUpperCase() !== selectedTaluk.toUpperCase()) return false;
      // For hobli, use the matchesHobli helper that handles English key to Kannada name mapping
      if (selectedHobli && !matchesHobli(app.hobli, selectedHobli)) return false;
      // For circle, use exact match (Kannada names)
      if (selectedCircle && app.circle !== selectedCircle) return false;
      // For village, use exact match first, then try case-insensitive (for Kannada names)
      if (selectedVillage) {
        // Try exact match first (for Kannada village names)
        if (app.village === selectedVillage) return true;
        // Then try case-insensitive match (for English village names)
        if (app.village?.toUpperCase() === selectedVillage.toUpperCase()) return true;
        return false;
      }
      return true;
    });
  }, [applications, selectedDistrict, selectedTaluk, selectedHobli, selectedCircle, selectedVillage, hoblis]);

  // Pagination calculations for detailed view
  const totalFilteredCount = filteredApplications.length;
  const totalPages = Math.ceil(totalFilteredCount / pageSize);
  const paginatedApplications = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredApplications.slice(startIndex, startIndex + pageSize);
  }, [filteredApplications, currentPage, pageSize]);
  
  // Reset to page 1 when filters change (but not on initial mount or when restoring from session)
  useEffect(() => {
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      return;
    }
    // Don't reset if we just restored from session storage
    if (restoredFromSessionRef.current) {
      return;
    }
    setCurrentPage(1);
  }, [selectedDistrict, selectedTaluk, selectedHobli, selectedVillage]);

  // Helper function to normalize hobli name for stats grouping
  const normalizeHobliName = (hobliName) => {
    if (!hobliName) return hobliName;
    // Normalize all Madabal variants to canonical 'ಮಾಡಬಾಳು'
    if (isMadabalVariant(hobliName)) {
      return 'ಮಾಡಬಾಳು';
    }
    return hobliName;
  };

  // Generate location-based statistics
  const locationStats = useMemo(() => {
    const stats = {
      byDistrict: {},
      byTaluk: {},
      byHobli: {},
      byCircle: {},
      byVillage: {},
      byStatus: {},
      total: filteredApplications.length
    };

    filteredApplications.forEach(app => {
      // District level
      if (app.district) {
        if (!stats.byDistrict[app.district]) {
          stats.byDistrict[app.district] = { total: 0, pending: 0, approved: 0, rejected: 0 };
        }
        stats.byDistrict[app.district].total++;
        if (app.status === "approved") stats.byDistrict[app.district].approved++;
        else if (app.status === "rejected") stats.byDistrict[app.district].rejected++;
        else stats.byDistrict[app.district].pending++;
      }

      // Taluk level
      if (app.taluk) {
        const talukKey = `${app.district}|${app.taluk}`;
        if (!stats.byTaluk[talukKey]) {
          stats.byTaluk[talukKey] = { district: app.district, taluk: app.taluk, total: 0, pending: 0, approved: 0, rejected: 0 };
        }
        stats.byTaluk[talukKey].total++;
        if (app.status === "approved") stats.byTaluk[talukKey].approved++;
        else if (app.status === "rejected") stats.byTaluk[talukKey].rejected++;
        else stats.byTaluk[talukKey].pending++;
      }

      // Hobli level - normalize Madabalu/Madabal to single entry
      if (app.hobli) {
        const normalizedHobli = normalizeHobliName(app.hobli);
        const hobliKey = `${app.district}|${app.taluk}|${normalizedHobli}`;
        if (!stats.byHobli[hobliKey]) {
          stats.byHobli[hobliKey] = { district: app.district, taluk: app.taluk, hobli: normalizedHobli, total: 0, pending: 0, approved: 0, rejected: 0 };
        }
        stats.byHobli[hobliKey].total++;
        if (app.status === "approved") stats.byHobli[hobliKey].approved++;
        else if (app.status === "rejected") stats.byHobli[hobliKey].rejected++;
        else stats.byHobli[hobliKey].pending++;
      }

      // Circle level - use normalized hobli
      if (app.circle) {
        const normalizedHobli = normalizeHobliName(app.hobli);
        const circleKey = `${app.district}|${app.taluk}|${normalizedHobli}|${app.circle}`;
        if (!stats.byCircle[circleKey]) {
          stats.byCircle[circleKey] = { district: app.district, taluk: app.taluk, hobli: normalizedHobli, circle: app.circle, total: 0, pending: 0, approved: 0, rejected: 0 };
        }
        stats.byCircle[circleKey].total++;
        if (app.status === "approved") stats.byCircle[circleKey].approved++;
        else if (app.status === "rejected") stats.byCircle[circleKey].rejected++;
        else stats.byCircle[circleKey].pending++;
      }

      // Village level - use normalized hobli
      if (app.village) {
        const normalizedHobli = normalizeHobliName(app.hobli);
        const villageKey = `${app.district}|${app.taluk}|${normalizedHobli}|${app.circle}|${app.village}`;
        if (!stats.byVillage[villageKey]) {
          stats.byVillage[villageKey] = { district: app.district, taluk: app.taluk, hobli: normalizedHobli, circle: app.circle, village: app.village, total: 0, pending: 0, approved: 0, rejected: 0 };
        }
        stats.byVillage[villageKey].total++;
        if (app.status === "approved") stats.byVillage[villageKey].approved++;
        else if (app.status === "rejected") stats.byVillage[villageKey].rejected++;
        else stats.byVillage[villageKey].pending++;
      }

      // Status breakdown
      if (!stats.byStatus[app.status]) {
        stats.byStatus[app.status] = 0;
      }
      stats.byStatus[app.status]++;
    });

    return stats;
  }, [filteredApplications]);

  const getCurrentLevel = () => {
    if (selectedVillage) return "village";
    if (selectedCircle) return "circle";
    if (selectedHobli) return "hobli";
    if (selectedTaluk) return "taluk";
    if (selectedDistrict) return "district";
    return "all";
  };

  if (loading) {
    return <LoadingOverlay fullPage={false} />;
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-lg sm:text-xl font-bold text-slate-900 flex items-center gap-2">
            <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-700" />
            {t('locationReports.title')}
          </h2>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">{t('locationReports.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={loadApplications}
            className="text-slate-600"
            data-testid="refresh-button"
          >
            <RefreshCw className="w-4 h-4 sm:mr-1" />
            <span className="hidden sm:inline">{t('common.refresh')}</span>
          </Button>
        </div>
      </div>

      {/* Location Filters */}
      <LocationFilters
        selectedDistrict={selectedDistrict}
        setSelectedDistrict={setSelectedDistrict}
        selectedTaluk={selectedTaluk}
        setSelectedTaluk={setSelectedTaluk}
        selectedHobli={selectedHobli}
        setSelectedHobli={setSelectedHobli}
        selectedVillage={selectedVillage}
        setSelectedVillage={setSelectedVillage}
        districts={districts}
        taluks={taluks}
        hoblis={hoblis}
        villages={villages}
        ENABLED_DISTRICT={ENABLED_DISTRICT}
        ENABLED_TALUK={ENABLED_TALUK}
        setViewMode={setViewMode}
        user={user}
      />

      {/* Summary Statistics */}
      <LocationStats locationStats={locationStats} />

      {/* View Mode Toggle */}
      <div className="flex gap-2">
        <Button
          variant={viewMode === "summary" ? "default" : "outline"}
          size="sm"
          onClick={() => {
            // When switching from Detailed to Summary View, reverse the drill-down
            // Clear village selection so Summary View shows the previous level
            // For Survey: Detailed → Village-wise (clear village, keep hobli)
            // For Revenue: Detailed → Circle-wise (clear village, keep circle)
            if (viewMode === "detailed" && selectedVillage) {
              setSelectedVillage("");
            }
            setViewMode("summary");
          }}
          className={viewMode === "summary" ? "bg-emerald-800 hover:bg-emerald-900" : ""}
          data-testid="view-summary"
        >
          <BarChart3 className="w-4 h-4 mr-1" />
          {t('locationReports.summaryView')}
        </Button>
        <Button
          variant={viewMode === "detailed" ? "default" : "outline"}
          size="sm"
          onClick={() => setViewMode("detailed")}
          className={viewMode === "detailed" ? "bg-emerald-800 hover:bg-emerald-900" : ""}
          data-testid="view-detailed"
        >
          <FileText className="w-4 h-4 mr-1" />
          {t('locationReports.detailedView')}
        </Button>
      </div>

      {/* Summary View */}
      {viewMode === "summary" && (
        <LocationSummaryView
          locationStats={locationStats}
          currentLevel={getCurrentLevel()}
          selectedDistrict={selectedDistrict}
          selectedTaluk={selectedTaluk}
          selectedHobli={selectedHobli}
          selectedCircle={selectedCircle}
          setSelectedDistrict={setSelectedDistrict}
          setSelectedTaluk={setSelectedTaluk}
          setSelectedHobli={setSelectedHobli}
          setSelectedCircle={setSelectedCircle}
          setSelectedVillage={setSelectedVillage}
          setViewMode={setViewMode}
          filteredApplications={filteredApplications}
          hoblis={hoblis}
          circles={circles}
          villages={villages}
          matchesHobli={matchesHobli}
          user={user}
        />
      )}

      {/* Detailed View */}
      {viewMode === "detailed" && (
        <LocationDetailedView
          paginatedApplications={paginatedApplications}
          currentPage={currentPage}
          totalPages={totalPages}
          totalCount={totalFilteredCount}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          tableLoading={tableLoading}
        />
      )}
    </div>
  );
}
